import { Injectable } from '@angular/core';
import { ConfirmationDialogAfterClosedModel, ConfirmationDialogDataModel } from './models/confirmation-dialog.model';
import { ConfirmationDialogActions } from './constants/confirmation-dialog-actions.constant';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';
import { DIALOG_DEFAULT_CONFIG } from '../../common/constants/dialog-config';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

type ConfirmationDialogModel<T> = MatDialogRef<
  T & { confirmed$?: BehaviorSubject<ConfirmationDialogAfterClosedModel> }
>;

@Injectable({
  providedIn: 'root',
})
export class ConfirmationDialogService {
  private isConfirmed$ = new BehaviorSubject<ConfirmationDialogAfterClosedModel>(null);
  private isCancelled$ = new BehaviorSubject<ConfirmationDialogAfterClosedModel>(null);
  private _isClosed$: Subject<boolean> = new Subject<boolean>();
  isClosed$: Observable<boolean> = this._isClosed$.asObservable();

  constructor(private dialog: MatDialog) {}

  open(data: ConfirmationDialogDataModel): MatDialogRef<ConfirmationDialogComponent> {
    const { id, disableClose, ...rest } = data;

    this._isClosed$.next(false);

    const dialog: ConfirmationDialogModel<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {
      ...DIALOG_DEFAULT_CONFIG,
      id,
      disableClose: disableClose ?? false,
      data: rest,
    });

    this.dialogConfirmationSubscription(dialog);
    this.resetConfirmationSubscription(dialog);
    this.dialogClosedSubscription(dialog);

    return dialog;
  }

  getConfirmationByDialogAction$(id: ConfirmationDialogActions): Observable<ConfirmationDialogAfterClosedModel> {
    return this.isConfirmed$.pipe(
      filter((data: ConfirmationDialogAfterClosedModel) => data && data.dialogRef.id === id),
    );
  }
  getCancellationByDialogAction$(id: ConfirmationDialogActions): Observable<ConfirmationDialogAfterClosedModel> {
    return this.isCancelled$.pipe(
      filter((data: ConfirmationDialogAfterClosedModel) => data && data.dialogRef.id === id),
    );
  }

  private dialogClosedSubscription(dialog: ConfirmationDialogModel<ConfirmationDialogComponent>): void {
    dialog.componentInstance.cancelled$
      .pipe(filter(Boolean), take(1))
      .subscribe((data: ConfirmationDialogAfterClosedModel) => this.isCancelled$.next(data));
  }

  private dialogConfirmationSubscription(dialog: ConfirmationDialogModel<ConfirmationDialogComponent>): void {
    dialog.componentInstance.confirmed$
      .pipe(filter(Boolean), take(1))
      .subscribe((data: ConfirmationDialogAfterClosedModel) => this.isConfirmed$.next(data));
  }

  private resetConfirmationSubscription(dialog: ConfirmationDialogModel<ConfirmationDialogComponent>): void {
    dialog
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this._isClosed$.next(true); // Unsubscribe in case when user click 'Cancel'
        this.isConfirmed$.next(null);
        this.isCancelled$.next(null);
      });
  }
}
